import { APP_VERSION } from '@/data/import-export/export';

/**
 * 桌面版的更新檢查（desktop-app.md 5.5）。
 *
 * 啟動時 fetch `version.json`，比對後**只在畫面角落顯示一行提示與下載連結**，
 * 不自動下載、不阻擋使用。抓不到（離線、逾時、CORS）必須完全靜默。
 *
 * 最重要的一件事：**必須先驗 `content-type`**。
 * `public/_redirects` 是 `/* /index.html 200`，所以 `version.json` 不存在時
 * 拿到的是**狀態碼 200 的 HTML**，不是 404。只判斷 `res.ok` 會通過，
 * 接著才在 `res.json()` 爆掉——而因為設計上「抓不到就靜默」，
 * 某次發版忘了產生 version.json 時，症狀會是「所有人都收不到更新提示」
 * 而且沒有任何錯誤訊息。這是 Phase 15 spike 最有價值的發現。
 */

export const VERSION_ENDPOINT = 'https://gentle-task.pages.dev/version.json';

/** 超過這個時間就放棄；啟動流程不該為了檢查更新而卡住。 */
export const UPDATE_CHECK_TIMEOUT_MS = 6000;

export interface VersionManifest {
  version: string;
  exeUrl: string;
  notes?: string;
}

export interface UpdateAvailable {
  latest: string;
  current: string;
  exeUrl: string;
  notes: string | null;
}

/** JSON 以外的 content-type 一律當作沒抓到。 */
export function isJsonContentType(value: string | null): boolean {
  if (!value) return false;
  return value.split(';')[0]?.trim().toLowerCase() === 'application/json';
}

export function parseVersionManifest(raw: unknown): VersionManifest | null {
  if (!raw || typeof raw !== 'object') return null;
  const candidate = raw as Partial<VersionManifest>;
  if (typeof candidate.version !== 'string' || candidate.version.trim() === '') return null;
  if (typeof candidate.exeUrl !== 'string' || !candidate.exeUrl.startsWith('https://')) return null;
  return {
    version: candidate.version.trim(),
    exeUrl: candidate.exeUrl,
    ...(typeof candidate.notes === 'string' ? { notes: candidate.notes } : {}),
  };
}

/**
 * 語意化版本比較。回傳 1 代表 a 比 b 新。
 * 只比數字段落，`0.3.0-beta.1` 的後綴一律忽略——發布流程不出 pre-release，
 * 真的出現時「不提示」比「亂提示」安全。
 */
export function compareVersions(a: string, b: string): number {
  const parse = (value: string) =>
    value
      .split('-')[0]!
      .split('.')
      .map((part) => Number.parseInt(part, 10))
      .map((part) => (Number.isFinite(part) ? part : 0));

  const left = parse(a);
  const right = parse(b);
  const length = Math.max(left.length, right.length);

  for (let index = 0; index < length; index += 1) {
    const diff = (left[index] ?? 0) - (right[index] ?? 0);
    if (diff !== 0) return diff > 0 ? 1 : -1;
  }
  return 0;
}

export interface FetchOptions {
  endpoint?: string;
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
}

/**
 * 抓 `version.json` 並驗證格式，不做版本比較。
 *
 * 更新檢查（\`checkForUpdate\`）與瀏覽器版設定頁的「下載本機版」卡片共用這個函式：
 * 兩者都需要「抓 manifest、驗 content-type」這段，差別只在前者要比版本、
 * 後者只是要顯示下載連結。**永遠不丟例外**，抓不到就回 null。
 */
export async function fetchVersionManifest(
  options: FetchOptions = {},
): Promise<VersionManifest | null> {
  const endpoint = options.endpoint ?? VERSION_ENDPOINT;
  const doFetch = options.fetchImpl ?? (typeof fetch === 'function' ? fetch : undefined);
  if (!doFetch) return null;

  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), options.timeoutMs ?? UPDATE_CHECK_TIMEOUT_MS)
    : null;

  try {
    const response = await doFetch(endpoint, {
      // 沒有 no-cache，使用者會拿到 Cloudflare 邊緣快取的舊 manifest。
      cache: 'no-cache',
      ...(controller ? { signal: controller.signal } : {}),
    });
    if (!response.ok) return null;
    if (!isJsonContentType(response.headers.get('content-type'))) return null;

    return parseVersionManifest(await response.json());
  } catch {
    // 離線、逾時、CORS、JSON 壞掉都走這裡，一律靜默。
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export interface CheckOptions extends FetchOptions {
  currentVersion?: string;
}

/**
 * 有新版就回傳它，其餘一律回 null（含所有錯誤）。
 * 這個函式**永遠不丟例外**：更新提示是加分項，不該讓啟動流程有機會壞掉。
 */
export async function checkForUpdate(options: CheckOptions = {}): Promise<UpdateAvailable | null> {
  const current = options.currentVersion ?? APP_VERSION;
  const manifest = await fetchVersionManifest(options);
  if (!manifest) return null;
  if (compareVersions(manifest.version, current) <= 0) return null;

  return {
    latest: manifest.version,
    current,
    exeUrl: manifest.exeUrl,
    notes: manifest.notes ?? null,
  };
}
